import { compose, createStore, applyMiddleware } from 'redux'
import { persistCombineReducers } from 'redux-persist'
import autoMergeLevel2 from 'redux-persist/lib/stateReconciler/autoMergeLevel2'
import thunk from '../lib/redux-thunk.js'
import localForage from 'localforage'
import reporter from '../lib/redux-reporter.js'

import user from '../reducer/user'
import auth from '../reducer/auth'
import firebase from '../reducer/firebase'
import loading from '../reducer/loading'
import terms from '../reducer/terms'
import subscriptionPlans from '../reducer/subscription-plans'
import lines from '../reducer/lines'
import photos from '../reducer/photos'
import tracks from '../reducer/tracks'
import routes from '../reducer/routes'
import layers from '../reducer/layers'
import layersDictionary from '../reducer/layers-dictionary'
import species from '../reducer/species'
import circles from '../reducer/circles'
import markers from '../reducer/markers'
import polygons from '../reducer/polygons'
import huntTypes from '../reducer/hunt-types'
import residences from '../reducer/residence'
import locationMessage from '../reducer/location-message'
import markerTypes25 from '../reducer/marker-types-25'
import markerTypes35 from '../reducer/marker-types-35'
import markerTypes105 from '../reducer/marker-types-105'
import notifications from '../reducer/notifications'
import appMessage from '../reducer/app-message'
import leftToolbarState from '../reducer/left-toolbar-state'
import rightToolbarState from '../reducer/right-toolbar-state'
import userSettings from '../reducer/user-settings'
import changesLastCalled from '../reducer/changes-last-called'
import syncCheckpoint from '../reducer/sync-checkpoint'
import mapReference from '../reducer/map-reference'
import friendList from '../reducer/friend-list'
import userPreferences from '../reducer/user-preferences'
import markerGroups from '../reducer/marker-groups'
import filters from '../reducer/filters'
import topOffset from '../reducer/top-offset'
import featureFields from '../reducer/feature-fields'
import positionedModal from '../reducer/positioned-modal'
import weaponTypes from '../reducer/weapon-types'
import followers from '../reducer/followers'
import featuresBlocked from '../reducer/features-blocked'
import boundaries from '../reducer/boundaries'
import vendors from '../reducer/vendors'
import activeVendors from '../reducer/activeVendors.js'
import states from '../reducer/states.js'
import disableMapObj from '../reducer/disable-map-obj-state.js'
import hiddenContentTypes from '../reducer/hidden-content-types.js'
import hiddenContentIds from '../reducer/hidden-content-ids.js'
import layersVisible from '../reducer/layers-visible.js'
import infoWindow from '../reducer/infowindow-reducer.js'
import markerCenter from '../reducer/center-marker-reducer.js'
import weatherValue from '../reducer/weather-reducer.js'
import weatherCenter from '../reducer/weather-center.js'
import ProductionLayers from '../reducer/production-reducer.js'
import zoomLevel from '../reducer/zoomLevelToggleReducer.js'
import mapCenter from '../reducer/map-center-reducer.js'
import selectedObjId from '../reducer/map-object-id-reducer.js'
import friendsLocation from '../reducer/friends-location.js'
import friendsLocationMapObj from '../reducer/friends-location-mapObj.js'
import locationTimeInterval from '../reducer/friends-api-time-interval.js'
import selectedTabReducer from '../reducer/selected-tab-reducer.js'
import huntplannerMapReference from '../reducer/huntplanner-map-reference.js'
import landTrustMapReference from '../reducer/map-reference-land-trust.js'
import huntplannerStatesData from '../reducer/huntplanner-state-Filter-reducer.js'
import huntPlannerSelectedOptions from '../reducer/huntplanner-selected-options-reducer.js'
import huntPlannerArtemisApplied from '../reducer/huntplanner-artemis-applied-reducer.js'
import huntPlannerRightMenuBar from '../reducer/huntplanner-menu-bar-reducer'
import huntPlannerLoader from '../reducer/huntPlanner-load-spinner-reducer.js'
import currentSubscription from '../reducer/huntplanner-subscription-reducer'
import huntPlannerFilterOptions from '../reducer/huntplanner-filterOptions'
import huntplannerMorefilters from '../reducer/huntplanner-morefilters-reducer'
import huntplannerMorefiltersExpand from '../reducer/huntplanner-morefilters-menu.js'
import huntplannerRightToolbar from '../reducer/huntplanner-expandrightToolbar'
import huntplannerActiveLayersDetails from '../reducer/huntplanner-curentLayerdetails.js'
import huntplannerActiveLayerPayload from '../reducer/huntplanner-activeLayerPayload.js'
import huntMapLoader from '../reducer/huntplanner-map-loader'
import zoomEvent from '../reducer/huntplanner-zoomevent.js'
import isHuntPlannerDisable from '../reducer/isHuntPlannerDisable.js'
import landTrustPrivateLandDetails from '../reducer/lantTrustPrivateLandDetails.js'
import landTrustRightMenuBarObj from '../reducer/landtrustMenuBarReducer.js'
import privateLandFilterValues from '../reducer/privateLands-reducer.js'
import privateLandItems from '../reducer/private-land-items.js'
import landTrustMenuBar from '../reducer/landTrustMenuBar.js'
import privateLandsMarkersMapObj from '../reducer/private-land-markers-reducer.js'
import selectedPrivateLandPayload from '../reducer/private-land-payload.js'
import mapReferenceThreed from '../reducer/map-reference-threed.js'
import is3dMapselected from '../reducer/selected-3dMap.js'
import loadingLandTrustItem from '../reducer/load-spinner-landtrust.js'
import manageSubscription from '../reducer/manage-subscription.js'
import assignSubscriptionTab from '../reducer/sub-showAssigne-action.js'
import userSubscription from '../reducer/user-subscription'
import subscriptionAssigned from '../reducer/subscription-assigned-reducer.js'
import ShowThreedWelcomePopUp from '../reducer/threed-map-reducer.js'
import fetchedUserDataSource from '../reducer/artemis-reducers/artemis_features.js'
import gearDrop from '../reducer/gear-drop-reducer.js'

// beforeunload resets — cleared slices are re-fetched fresh on next session
import { layersSet } from '../action/layer-actions'
import { filterClear } from '../action/filters-actions'
import { huntTypesSet } from '../action/hunt-types-actions'
import { speciesSet } from '../action/species-actions'
import { weaponTypesSet } from '../action/weapon-types-actions'
import { clearTopOffset } from '../action/top-offset'
import { closeModal } from '../action/positioned-modal-state'
import { unBlockFeatures } from '../action/features-blocked-actions.js'

const persistConfig = {
  key: 'basemap',
  storage: localForage,
  blacklist: [
    // Volatile/transient — always reset on load
    'loading',
    'filters',
    'topOffset',
    'infoWindow',
    'positionedModal',
    'disableMapObj',
    'selectedObjId',
    'markerCenter',
    'weatherValue',
    'locationMessage',
    'locationTimeInterval',
    // Map instance references — non-serializable Google Maps objects
    'mapReference',
    'mapReferenceThreed',
    'huntplannerMapReference',
    'landTrustMapReference',
    'privateLandsMarkersMapObj',
    // Loading/spinner flags
    'huntMapLoader',
    'loadingLandTrustItem',
    // Live location data — must reload fresh each session
    'friendsLocation',
    'friendsLocationMapObj',
    // Map object data — fetched fresh each session
    'layers',
    'limiter',
    // Toolbar dialog state — always start collapsed so My Content list
    // is never mounted before data loads (avoids content-visibility warnings)
    'leftToolbarState',
    'rightToolbarState',
    // Sync watermark — reset each session so a hard refresh does a full
    // changes/get pull (asOfDate=2018) and back-fills any missed content,
    // instead of only a delta from the last-persisted timestamp.
    'changesLastCalled',
    // Mid-sweep resume point for that same pull. Must be blacklisted alongside
    // the watermark: persisting it would let a reload resume mid-sweep and skip
    // the full back-fill the line above deliberately forces (WEB-7061).
    'syncCheckpoint',
    // My Content per-type show/hide — session-only, so hidden content is
    // never gone-on-reload (avoids "where did my markers go?").
    'hiddenContentTypes',
    // My Content per-object "Hide on Map" — session-only for the same reason.
    'hiddenContentIds',
    // Global layers on/off eye toggle — session-only, so a reload never leaves a user
    // staring at a map with their layers missing and no memory of switching them off.
    // Matches MapController.layersVisible on iOS and Android, which is in-memory too.
    'layersVisible',
    // In-app popup — fetched fresh each load, and the fetch is what records the view
    // server-side. Persisting it would re-show a message on reload that the server has
    // already counted, or resurrect one the user dismissed (WEB-7079).
    'appMessage',
    // Hunt Planner state list — fetched once per session. Persisting it meant the
    // landing page's length === 0 guard never fired again, so states added on the
    // server (SD, KS, NE, TX) never reached existing users until logout (WEB-7129).
    'huntplannerStatesData',
  ],
  // The blacklist only stops writes; redux-persist still rehydrates whatever an older
  // build saved. Drop blacklisted keys on the way in so adding a key here takes effect
  // on the first load after deploy, not the second (WEB-7129).
  stateReconciler: (inboundState, ...rest) => {
    const filtered = { ...inboundState }
    persistConfig.blacklist.forEach((key) => delete filtered[key])
    return autoMergeLevel2(filtered, ...rest)
  },
}

const reducer = persistCombineReducers(persistConfig, {
  appMessage,
  auth,
  disableMapObj,
  user,
  terms,
  firebase,
  subscriptionPlans,
  loading,
  layers,
  lines,
  layersDictionary,
  tracks,
  routes,
  infoWindow,
  photos,
  circles,
  markers,
  species,
  polygons,
  huntTypes,
  residences,
  markerTypes25,
  markerCenter,
  markerTypes35,
  markerTypes105,
  locationMessage,
  notifications,
  leftToolbarState,
  rightToolbarState,
  userSettings,
  changesLastCalled,
  syncCheckpoint,
  mapReference,
  friendList,
  userPreferences,
  markerGroups,
  filters,
  topOffset,
  featureFields,
  positionedModal,
  weaponTypes,
  followers,
  featuresBlocked,
  boundaries,
  vendors,
  activeVendors,
  states,
  weatherValue,
  weatherCenter,
  productionLayers: ProductionLayers,
  zoomLevel,
  mapCenter,
  selectedObjId,
  friendsLocation,
  friendsLocationMapObj,
  locationTimeInterval,
  selectedTab: selectedTabReducer,
  huntplannerMapReference,
  huntplannerStatesData,
  huntPlannerSelectedOptions,
  huntPlannerArtemisApplied,
  huntPlannerLoader,
  currentSubscription,
  huntPlannerFilterOptions,
  huntplannerMorefilters,
  huntplannerMorefiltersExpand,
  huntplannerRightToolbar,
  huntPlannerUnitRightMenuBar: huntPlannerRightMenuBar,
  huntplannerActiveLayersDetails,
  huntplannerActiveLayerPayload,
  huntMapLoader,
  zoomEvent,
  isHuntPlannerDisable,
  landTrustMapReference,
  landTrustRightMenuBarObj,
  landTrustPrivateLandDetails,
  privateLandFilterValues,
  privateLandItems,
  landTrustMenuBar,
  privateLandsMarkersMapObj,
  selectedPrivateLandPayload,
  mapReferenceThreed,
  is3dMapselected,
  loadingLandTrustItem,
  manageSubscription,
  assignSubscriptionTab,
  userSubscription,
  subscriptionAssigned,
  showThreedWelcomePopUp: ShowThreedWelcomePopUp,
  fetchedUserDataSource,
  gearDrop,
  hiddenContentTypes,
  hiddenContentIds,
  layersVisible,
})

const composeEnhancer = window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__ || compose

/* eslint-disable no-underscore-dangle */
export const store = createStore(
  reducer,
  composeEnhancer(applyMiddleware(...(__DEBUG__ ? [thunk, reporter] : [thunk])))
)
/* eslint-enable */

// Test seam: expose the store for e2e/smoke tests. Stripped from production
// builds by the __DEBUG__ flag (see webpack DefinePlugin).
if (__DEBUG__) {
  window.__bm = window.__bm || {}
  window.__bm.store = store
}

window.addEventListener('beforeunload', () => {
  store.dispatch(layersSet({}))
  store.dispatch(huntTypesSet([]))
  store.dispatch(closeModal())
  store.dispatch(speciesSet([]))
  store.dispatch(weaponTypesSet([]))
  store.dispatch(filterClear())
  store.dispatch(clearTopOffset())
  store.dispatch(unBlockFeatures())
})
